import { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../config/api';

export const DEFAULT_ONBOARDING_EMAIL_HTML_TEMPLATE = `<div style="font-family: Arial, sans-serif; color: #17324b; line-height: 1.5;">
  <h1>Welcome {name} {surname} to car-pool.app</h1>
  <p>Your school has created a parent account for you.</p>
  <p>Download the parent app, then enter the email or phone number your school has on file. We will send a 6-digit verification code to your email so you can create your password.</p>
  <p>Email on file: <strong>{email}</strong></p>
  <p>Phone on file: <strong>{phone}</strong></p>
  <p>Download the parent app here:</p>
  <p><a href="{parent_app_appstore_link}">App Store</a></p>
  <p><a href="{parent_app_playstore_link}">Google Play</a></p>
</div>`;

export const DEFAULT_ONBOARDING_EMAIL_TEXT_TEMPLATE = `Welcome {name} {surname} to car-pool.app

Your school has created a parent account for you.

Download the parent app, then enter the email or phone number your school has on file. We will send a 6-digit verification code to your email so you can create your password.

Email on file: {email}
Phone on file: {phone}

Download the parent app here:
App Store: {parent_app_appstore_link}
Google Play: {parent_app_playstore_link}`;

export const DEFAULT_STAFF_ONBOARDING_EMAIL_HTML_TEMPLATE = `<div style="font-family: Arial, sans-serif; color: #17324b; line-height: 1.5;">
  <h1>Welcome {name} {surname} to Car-Pool Staff</h1>
  <p>{school_name} has created a Car-Pool staff account for you.</p>
  <p>Your staff account is registered under <strong>{email}</strong>. Download the Car-Pool Staff app, enter that email address, and follow the instructions in the app to claim your account.</p>
  <p>Download the staff app here:</p>
  <p><a href="{staff_app_appstore_link}">App Store</a></p>
  <p><a href="{staff_app_playstore_link}">Google Play</a></p>
  <p><strong>Need help?</strong></p>
  <p>Contact the Car-Pool support team at <a href="mailto:support@car-pool.app">support@car-pool.app</a>.</p>
</div>`;

export const DEFAULT_STAFF_ONBOARDING_EMAIL_TEXT_TEMPLATE = `Welcome {name} {surname} to Car-Pool Staff

{school_name} has created a Car-Pool staff account for you.

Your staff account is registered under {email}. Download the Car-Pool Staff app, enter that email address, and follow the instructions in the app to claim your account.

Download the staff app here:
App Store: {staff_app_appstore_link}
Google Play: {staff_app_playstore_link}

Need help?

Contact the Car-Pool support team at support@car-pool.app.`;

const FALLBACK_PARENT_APP_APPSTORE_LINK = 'https://apps.apple.com/us/app/car-pool/id6762562471';
const FALLBACK_PARENT_APP_PLAYSTORE_LINK = 'https://play.google.com/store/apps/details?id=com.nativ.carpool';
const FALLBACK_STAFF_APP_APPSTORE_LINK = 'https://apps.apple.com/us/app/car-pool-staff/id6762563234';
const FALLBACK_STAFF_APP_PLAYSTORE_LINK = 'https://play.google.com/store/apps/details?id=com.nativ.carpool.staff';

function getEnvValue(key) {
  return String(process.env[key] || '').trim();
}

const PARENT_APP_APPSTORE_LINK = getEnvValue('REACT_APP_PARENT_APP_APPSTORE_LINK') || FALLBACK_PARENT_APP_APPSTORE_LINK;
const PARENT_APP_PLAYSTORE_LINK = getEnvValue('REACT_APP_PARENT_APP_PLAYSTORE_LINK') || FALLBACK_PARENT_APP_PLAYSTORE_LINK;
const STAFF_APP_APPSTORE_LINK = getEnvValue('REACT_APP_STAFF_APP_APPSTORE_LINK') || FALLBACK_STAFF_APP_APPSTORE_LINK;
const STAFF_APP_PLAYSTORE_LINK = getEnvValue('REACT_APP_STAFF_APP_PLAYSTORE_LINK') || FALLBACK_STAFF_APP_PLAYSTORE_LINK;

const HTML_REPLACEMENTS = {
  '{name}': 'Alex',
  '{surname}': 'Rivera',
  '{email}': 'alex.rivera@example.com',
  '{phone}': '(555) 123-4567',
  '{parent_app_appstore_link}': PARENT_APP_APPSTORE_LINK,
  '{parent_app_playstore_link}': PARENT_APP_PLAYSTORE_LINK,
};

const TEXT_REPLACEMENTS = HTML_REPLACEMENTS;

const STAFF_REPLACEMENTS = {
  '{name}': 'Alex',
  '{surname}': 'Rivera',
  '{email}': 'alex.rivera@school.edu',
  '{school_name}': 'Your School',
  '{staff_app_appstore_link}': STAFF_APP_APPSTORE_LINK,
  '{staff_app_playstore_link}': STAFF_APP_PLAYSTORE_LINK,
};

const PLACEHOLDER_TOKENS = [
  '{name}',
  '{surname}',
  '{email}',
  '{phone}',
  '{parent_app_appstore_link}',
  '{parent_app_playstore_link}',
];

const STAFF_PLACEHOLDER_TOKENS = [
  '{name}',
  '{surname}',
  '{email}',
  '{school_name}',
  '{staff_app_appstore_link}',
  '{staff_app_playstore_link}',
];

function pluralize(count, singular, plural = `${singular}s`) {
  return `${count} ${count === 1 ? singular : plural}`;
}

function summarizeRecipientsForBulkSend(recipients = [], audience = 'parent') {
  return recipients.reduce((summary, recipient) => {
    const hasEmail = Boolean(audience === 'staff' ? recipient.school_email : recipient.email);
    const hasPassword = Boolean(recipient.has_password);

    summary.total += 1;
    if (hasEmail) summary.withEmail += 1;
    else summary.missingEmail += 1;

    if (hasEmail && hasPassword) summary.alreadyOnboarded += 1;
    if (hasEmail && !hasPassword) summary.needsOnboarding += 1;
    if (audience === 'staff' && hasEmail && !recipient.last_login_at) summary.neverLoggedIn += 1;

    return summary;
  }, {
    total: 0,
    withEmail: 0,
    needsOnboarding: 0,
    alreadyOnboarded: 0,
    missingEmail: 0,
    neverLoggedIn: 0,
  });
}

function applyTemplateValues(template, replacements) {
  return Object.entries(replacements).reduce(
    (current, [token, value]) => current.split(token).join(value),
    String(template ?? '')
  );
}

function buildPreviewDocument(renderedHtml) {
  if (/<html[\s>]|<!doctype/i.test(renderedHtml)) {
    return renderedHtml;
  }

  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <style>
      body { margin: 0; padding: 20px; font-family: Arial, sans-serif; color: #17324b; background: #ffffff; }
      a { color: #1a5fb4; }
    </style>
  </head>
  <body>${renderedHtml}</body>
</html>`;
}

const ACTIVE_BULK_STATUSES = new Set(['queued', 'running', 'paused']);

function isBulkJobActive(job) {
  return ACTIVE_BULK_STATUSES.has(job?.status);
}

function formatDateTime(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatDurationSeconds(seconds) {
  const safeSeconds = Math.max(0, Math.ceil(Number(seconds) || 0));
  if (safeSeconds < 60) return safeSeconds <= 5 ? 'now' : '<1 min';

  const minutes = Math.ceil(safeSeconds / 60);
  if (minutes < 60) return `${minutes} min`;

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes
    ? `${hours} hr ${remainingMinutes} min`
    : `${hours} hr`;
}

function formatWindowSeconds(seconds) {
  const safeSeconds = Math.max(1, Number(seconds) || 1);

  if (safeSeconds % 3600 === 0) {
    const hours = safeSeconds / 3600;
    return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
  }

  if (safeSeconds % 60 === 0) {
    const minutes = safeSeconds / 60;
    return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
  }

  return `${safeSeconds} seconds`;
}

function formatRatePolicy(policy) {
  if (!policy?.limit || !policy?.window_seconds) return '';
  return `${policy.limit} ${policy.limit === 1 ? 'email' : 'emails'} every ${formatWindowSeconds(policy.window_seconds)}`;
}

function containsResetLinkToken(template) {
  return String(template || '').includes('{reset_password_link}');
}

function secondsUntil(value, now) {
  if (!value) return null;
  const timestamp = new Date(value).getTime();
  if (Number.isNaN(timestamp)) return null;
  return Math.max(0, Math.ceil((timestamp - now) / 1000));
}

function bulkProgressPercent(job) {
  const fallbackTargetCount = Number(job?.target_count) || 0;
  const fallbackProcessed = Number(job?.processed_count ?? job?.current_index) || 0;
  const rawPercent = Number.isFinite(Number(job?.progress_percent))
    ? Number(job.progress_percent)
    : fallbackTargetCount > 0
      ? (fallbackProcessed / fallbackTargetCount) * 100
      : 100;

  return Math.max(0, Math.min(100, Math.round(rawPercent)));
}

function bulkEtaText(job, now) {
  if (!job) return '';

  if (!isBulkJobActive(job)) {
    if (job.status === 'completed' || job.status === 'completed_with_failures') {
      return job.completed_at ? `Done ${formatDateTime(job.completed_at)}` : 'Done';
    }
    return '';
  }

  const seconds = secondsUntil(job.estimated_completion_at, now);
  if (seconds === null) return '';

  const etaTime = formatDateTime(job.estimated_completion_at);
  const remaining = formatDurationSeconds(seconds);
  return etaTime ? `ETA ${etaTime} (${remaining})` : `ETA ${remaining}`;
}

function bulkNextBatchText(job, now) {
  if (!isBulkJobActive(job) || !job?.next_batch_at || !job?.remaining) return '';
  const seconds = secondsUntil(job.next_batch_at, now);
  if (seconds === null || seconds <= 5) return 'Next batch now';
  return `Next batch in ${formatDurationSeconds(seconds)}`;
}

function bulkStatusLabel(job) {
  switch (job?.status) {
    case 'queued':
      return 'Queued';
    case 'running':
      return 'Sending';
    case 'paused':
      return 'Paused';
    case 'completed_with_failures':
      return 'Complete with failures';
    case 'completed':
      return 'Complete';
    case 'failed':
      return 'Failed';
    default:
      return 'Result';
  }
}

function bulkPauseMessage(job) {
  const retryTime = formatDateTime(job?.next_retry_at);
  const retryText = retryTime ? ` The next batch starts after ${retryTime}.` : '';

  if (job?.rate_limit?.source === 'local_rate_window' || job?.rate_limit?.source === 'local_hourly_limit') {
    const rateText = formatRatePolicy(job.rate_limit) || formatRatePolicy(job.rate_policy) || 'the configured email cadence';
    return `Email cadence reached (${rateText}).${retryText}`;
  }

  return `Mail provider limit reached.${retryText}`;
}

function resolveSchoolTemplate(school) {
  const storedHtml = school?.onboarding_email_html_template;
  const storedText = school?.onboarding_email_text_template;

  return {
    html: storedHtml && !containsResetLinkToken(storedHtml)
      ? storedHtml
      : DEFAULT_ONBOARDING_EMAIL_HTML_TEMPLATE,
    text: storedText && !containsResetLinkToken(storedText)
      ? storedText
      : DEFAULT_ONBOARDING_EMAIL_TEXT_TEMPLATE,
  };
}

export default function SchoolOnboardingEmailField({
  schoolId,
  school,
  token,
  onSaved,
  disabled = false,
  audience = 'parent',
}) {
  const isStaff = audience === 'staff';
  const persistedTemplate = useMemo(
    () => isStaff
      ? {
        html: DEFAULT_STAFF_ONBOARDING_EMAIL_HTML_TEMPLATE,
        text: DEFAULT_STAFF_ONBOARDING_EMAIL_TEXT_TEMPLATE,
      }
      : resolveSchoolTemplate(school),
    [isStaff, school]
  );
  const [draft, setDraft] = useState(persistedTemplate);
  const [activeEditor, setActiveEditor] = useState('html');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedAt, setSavedAt] = useState(0);
  const [parentCounts, setParentCounts] = useState(() => ({
    loading: true,
    total: 0,
    withEmail: 0,
    needsOnboarding: 0,
    alreadyOnboarded: 0,
    missingEmail: 0,
    neverLoggedIn: 0,
  }));
  const [parentCountError, setParentCountError] = useState('');
  const [includeAlreadyOnboarded, setIncludeAlreadyOnboarded] = useState(false);
  const [staffNeverLoggedInOnly, setStaffNeverLoggedInOnly] = useState(false);
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkError, setBulkError] = useState('');
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkRefreshKey, setBulkRefreshKey] = useState(0);
  const [bulkNow, setBulkNow] = useState(Date.now());

  useEffect(() => {
    setDraft(persistedTemplate);
    setError('');
  }, [persistedTemplate]);

  useEffect(() => {
    setSavedAt(0);
    setBulkResult(null);
    setBulkError('');
    setIncludeAlreadyOnboarded(false);
    setStaffNeverLoggedInOnly(false);
  }, [schoolId]);

  useEffect(() => {
    let cancelled = false;

    setParentCounts((current) => ({ ...current, loading: true }));
    setParentCountError('');

    const recipientsPath = isStaff
      ? `/staff?school_id=${schoolId}&is_active=true`
      : `/contacts?school_id=${schoolId}&type=parent`;

    apiFetch(recipientsPath, {}, token)
      .then((result) => {
        if (cancelled) return;
        setParentCounts({
          loading: false,
          ...summarizeRecipientsForBulkSend(result.data || [], audience),
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setParentCounts({
          loading: false,
          total: 0,
          withEmail: 0,
          needsOnboarding: 0,
          alreadyOnboarded: 0,
          missingEmail: 0,
          neverLoggedIn: 0,
        });
        setParentCountError(err.message || `Could not load ${isStaff ? 'staff' : 'parent'} counts`);
      });

    return () => {
      cancelled = true;
    };
  }, [audience, bulkRefreshKey, isStaff, schoolId, token]);

  const renderedHtml = useMemo(
    () => applyTemplateValues(draft.html, isStaff ? STAFF_REPLACEMENTS : HTML_REPLACEMENTS),
    [draft.html, isStaff]
  );
  const renderedText = useMemo(
    () => applyTemplateValues(draft.text, isStaff ? STAFF_REPLACEMENTS : TEXT_REPLACEMENTS),
    [draft.text, isStaff]
  );
  const previewDocument = useMemo(() => buildPreviewDocument(renderedHtml), [renderedHtml]);

  const dirty = !isStaff && (draft.html !== persistedTemplate.html || draft.text !== persistedTemplate.text);
  const bulkTargetCount = isStaff && staffNeverLoggedInOnly
    ? parentCounts.neverLoggedIn
    : includeAlreadyOnboarded
      ? parentCounts.withEmail
      : parentCounts.needsOnboarding;
  const activeBulkJob = isBulkJobActive(bulkResult);
  const bulkButtonLabel = activeBulkJob
    ? bulkStatusLabel(bulkResult)
    : `Send to ${bulkTargetCount}`;
  const progressPercent = bulkProgressPercent(bulkResult);
  const etaText = bulkEtaText(bulkResult, bulkNow);
  const nextBatchText = bulkNextBatchText(bulkResult, bulkNow);
  const ratePolicyText = formatRatePolicy(bulkResult?.rate_policy);

  useEffect(() => {
    if (!activeBulkJob) return undefined;

    setBulkNow(Date.now());
    const interval = setInterval(() => setBulkNow(Date.now()), 10000);

    return () => clearInterval(interval);
  }, [activeBulkJob, bulkResult?.job_id, bulkResult?.status]);

  const updateDraft = (key) => (event) => {
    setDraft((current) => ({ ...current, [key]: event.target.value }));
    if (error) setError('');
  };

  const handleSave = async () => {
    setError('');
    setSaving(true);

    try {
      const result = await apiFetch(`/schools/${schoolId}`, {
        method: 'PUT',
        body: JSON.stringify({
          onboarding_email_html_template: draft.html,
          onboarding_email_text_template: draft.text,
        }),
      }, token);
      onSaved?.(result.data);
      setSavedAt(Date.now());
    } catch (err) {
      setError(err.message || 'Could not save onboarding email');
    } finally {
      setSaving(false);
    }
  };

  const handleResetToDefault = () => {
    setDraft({
      html: DEFAULT_ONBOARDING_EMAIL_HTML_TEMPLATE,
      text: DEFAULT_ONBOARDING_EMAIL_TEXT_TEMPLATE,
    });
    setActiveEditor('html');
    setError('');
  };

  useEffect(() => {
    if (!schoolId) return undefined;

    let cancelled = false;

    const activePath = isStaff
      ? `/staff/onboarding-email/bulk/active?school_id=${schoolId}`
      : `/contacts/onboarding-email/bulk/active?school_id=${schoolId}`;

    apiFetch(activePath, {}, token)
      .then((result) => {
        if (cancelled || !result.data) return;
        setBulkResult(result.data);
        setBulkSending(isBulkJobActive(result.data));
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [isStaff, schoolId, token]);

  useEffect(() => {
    const jobId = bulkResult?.job_id;
    const status = bulkResult?.status;
    if (!jobId || !ACTIVE_BULK_STATUSES.has(status)) return undefined;

    let cancelled = false;
    let refreshedAfterTerminal = false;

    const poll = async () => {
      try {
        const statusPath = isStaff
          ? `/staff/onboarding-email/bulk/${jobId}`
          : `/contacts/onboarding-email/bulk/${jobId}`;
        const result = await apiFetch(statusPath, {}, token);
        if (cancelled) return;

        const job = result.data;
        setBulkNow(Date.now());
        setBulkResult(job);
        setBulkSending(isBulkJobActive(job));

        if (!isBulkJobActive(job) && !refreshedAfterTerminal) {
          refreshedAfterTerminal = true;
          setBulkRefreshKey((current) => current + 1);
        }
      } catch (err) {
        if (!cancelled) setBulkError(err.message || 'Could not refresh onboarding email job');
      }
    };

    const firstPoll = setTimeout(poll, 1000);
    const interval = setInterval(poll, status === 'paused' ? 15000 : 3000);

    return () => {
      cancelled = true;
      clearTimeout(firstPoll);
      clearInterval(interval);
    };
  }, [bulkResult?.job_id, bulkResult?.status, isStaff, token]);

  const handleBulkSend = async () => {
    if (dirty) {
      setBulkError('Save email changes before sending onboarding emails.');
      return;
    }

    if (activeBulkJob) return;
    if (bulkTargetCount < 1) return;

    const recipientName = isStaff ? 'staff account' : 'parent account';
    const targetDescription = isStaff && staffNeverLoggedInOnly
      ? pluralize(bulkTargetCount, 'staff account that has never logged in', 'staff accounts that have never logged in')
      : includeAlreadyOnboarded
      ? pluralize(bulkTargetCount, recipientName)
      : pluralize(bulkTargetCount, `unclaimed ${recipientName}`);

    if (!window.confirm(`Send onboarding email to ${targetDescription}? ${isStaff ? 'Staff members' : 'Parents'} will be told to download the app and claim their account from the first screen. If the mail provider limit is reached, sending will pause and resume automatically.`)) {
      return;
    }

    setBulkSending(true);
    setBulkError('');
    setBulkResult(null);

    try {
      const bulkPath = isStaff ? '/staff/onboarding-email/bulk' : '/contacts/onboarding-email/bulk';
      const result = await apiFetch(bulkPath, {
        method: 'POST',
        body: JSON.stringify({
          school_id: schoolId,
          force: isStaff && staffNeverLoggedInOnly ? true : includeAlreadyOnboarded,
          never_logged_in_only: isStaff && staffNeverLoggedInOnly,
        }),
      }, token);

      setBulkResult(result.data);
      setBulkNow(Date.now());
      setBulkSending(isBulkJobActive(result.data));
      if (!isBulkJobActive(result.data)) setBulkRefreshKey((current) => current + 1);
    } catch (err) {
      setBulkError(err.message || 'Could not send onboarding emails');
      setBulkSending(false);
    }
  };

  return (
    <div className="ssp-email-field">
      <div className="ssp-email-token-row">
        {(isStaff ? STAFF_PLACEHOLDER_TOKENS : PLACEHOLDER_TOKENS).map((placeholder) => (
          <span className="ssp-email-token" key={placeholder}>{placeholder}</span>
        ))}
      </div>

      <div className="ssp-email-workspace">
        {isStaff ? (
          <div className="ssp-email-editor-card">
            <div className="ssp-email-preview-heading">
              <h3>Staff Email</h3>
              <span>Managed template</span>
            </div>
            <p className="ssp-email-bulk-note">
              Staff receive their school email address, Car-Pool Staff download links, and instructions for claiming their account in the app.
            </p>
          </div>
        ) : (
          <div className="ssp-email-editor-card">
          <div className="ssp-email-tabs" role="tablist" aria-label="Onboarding email editor">
            <button
              type="button"
              className={`ssp-email-tab ${activeEditor === 'html' ? 'active' : ''}`}
              onClick={() => setActiveEditor('html')}
              role="tab"
              aria-selected={activeEditor === 'html'}
            >
              HTML
            </button>
            <button
              type="button"
              className={`ssp-email-tab ${activeEditor === 'text' ? 'active' : ''}`}
              onClick={() => setActiveEditor('text')}
              role="tab"
              aria-selected={activeEditor === 'text'}
            >
              Text
            </button>
          </div>

          {activeEditor === 'html' ? (
            <textarea
              className="ssp-email-editor"
              value={draft.html}
              onChange={updateDraft('html')}
              spellCheck="false"
              disabled={disabled || saving}
              aria-label="Onboarding email HTML"
            />
          ) : (
            <textarea
              className="ssp-email-editor"
              value={draft.text}
              onChange={updateDraft('text')}
              spellCheck="false"
              disabled={disabled || saving}
              aria-label="Onboarding email plain text"
            />
          )}
          </div>
        )}

        <div className="ssp-email-preview-card">
          <div className="ssp-email-preview-heading">
            <h3>{isStaff ? 'Staff Email Preview' : 'Email Preview'}</h3>
            <span>Alex Rivera</span>
          </div>
          <iframe
            className="ssp-email-preview-frame"
            title="Onboarding email HTML preview"
            sandbox=""
            srcDoc={previewDocument}
          />
          <pre className="ssp-email-text-preview">{renderedText}</pre>
        </div>
      </div>

      {!isStaff ? <div className="ssp-email-actions">
        <button
          type="button"
          className="sa-btn sa-btn-primary"
          onClick={handleSave}
          disabled={disabled || saving || !dirty}
        >
          {saving ? 'Saving...' : 'Save Email'}
        </button>
        <button
          type="button"
          className="sa-btn sa-btn-secondary"
          onClick={handleResetToDefault}
          disabled={disabled || saving}
        >
          Reset to Default
        </button>
        {!dirty && savedAt ? <span className="ssp-default-times-saved">Saved</span> : null}
      </div> : null}

      {error ? <div className="sa-error ssp-email-error">{error}</div> : null}

      <div className="ssp-email-bulk-card">
        <div className="ssp-email-bulk-header">
          <div>
            <h3>Send Onboarding Email to {isStaff ? 'Staff' : 'Parents'}</h3>
            <p>
              {parentCounts.loading
                ? `Loading ${isStaff ? 'staff' : 'parent'} accounts...`
                : `${pluralize(parentCounts.total, isStaff ? 'staff account' : 'parent account')} found. ${pluralize(parentCounts.needsOnboarding, isStaff ? 'staff member' : 'parent')} can receive onboarding now.`}
            </p>
          </div>
          <button
            type="button"
            className="sa-btn sa-btn-primary"
            onClick={handleBulkSend}
            disabled={disabled || saving || bulkSending || activeBulkJob || dirty || parentCounts.loading || bulkTargetCount < 1}
          >
            {activeBulkJob ? bulkButtonLabel : (bulkSending ? 'Sending...' : bulkButtonLabel)}
          </button>
        </div>

        {isStaff ? (
          <label className="ssp-email-bulk-toggle">
            <input
              type="checkbox"
              checked={staffNeverLoggedInOnly}
              onChange={(event) => {
                setStaffNeverLoggedInOnly(event.target.checked);
                if (event.target.checked) setIncludeAlreadyOnboarded(false);
                setBulkError('');
                setBulkResult(null);
              }}
              disabled={disabled || bulkSending || activeBulkJob || parentCounts.loading}
            />
            <span>Send only to {pluralize(parentCounts.neverLoggedIn, 'staff member')} who never logged in, including claimed accounts</span>
          </label>
        ) : null}

        {!staffNeverLoggedInOnly ? <label className="ssp-email-bulk-toggle">
          <input
            type="checkbox"
            checked={includeAlreadyOnboarded}
            onChange={(event) => {
              setIncludeAlreadyOnboarded(event.target.checked);
              setBulkError('');
              setBulkResult(null);
            }}
            disabled={disabled || bulkSending || activeBulkJob || parentCounts.loading}
          />
          <span>Include {pluralize(parentCounts.alreadyOnboarded, isStaff ? 'staff member' : 'parent')} who already claimed their account</span>
        </label> : null}

        {dirty ? (
          <div className="ssp-email-bulk-note">Save the email template before sending it to parents.</div>
        ) : null}
        {parentCounts.missingEmail > 0 ? (
          <div className="ssp-email-bulk-note">{pluralize(parentCounts.missingEmail, isStaff ? 'staff member' : 'parent')} skipped because no email address is saved.</div>
        ) : null}
        {parentCountError ? <div className="sa-error ssp-email-error">{parentCountError}</div> : null}
        {bulkError ? <div className="sa-error ssp-email-error">{bulkError}</div> : null}

        {bulkResult ? (
          <div className="ssp-email-bulk-result">
            <strong>
              {bulkStatusLabel(bulkResult)}. Sent {bulkResult.summary?.sent || 0}. Skipped {bulkResult.summary?.skipped || 0}. Failed {bulkResult.summary?.failed || 0}. Pending {bulkResult.summary?.pending || 0}.
            </strong>
            {(bulkResult.target_count || 0) > 0 ? (
              <div className="ssp-email-bulk-progress-wrap">
                <div
                  className="ssp-email-bulk-progress-bar"
                  role="progressbar"
                  aria-label="Bulk onboarding email send progress"
                  aria-valuemin="0"
                  aria-valuemax="100"
                  aria-valuenow={progressPercent}
                >
                  <div className="ssp-email-bulk-progress-fill" style={{ width: `${progressPercent}%` }} />
                </div>
                <div className="ssp-email-bulk-progress-meta">
                  <span>Processing {bulkResult.processed_count ?? bulkResult.current_index ?? 0} of {bulkResult.target_count || 0} send targets</span>
                  {etaText ? <span className="ssp-email-bulk-eta">{etaText}</span> : null}
                </div>
                {nextBatchText || ratePolicyText ? (
                  <div className="ssp-email-bulk-progress-submeta">
                    {nextBatchText ? <span>{nextBatchText}</span> : null}
                    {ratePolicyText ? <span>{ratePolicyText}</span> : null}
                  </div>
                ) : null}
              </div>
            ) : null}
            {bulkResult.status === 'paused' ? (
              <div className="ssp-email-bulk-progress">
                {bulkPauseMessage(bulkResult)}
              </div>
            ) : null}
            {bulkResult.status === 'failed' && bulkResult.error ? (
              <div className="ssp-email-bulk-failures">{bulkResult.error}</div>
            ) : null}
            {bulkResult.failures?.length ? (
              <div className="ssp-email-bulk-failures">
                {bulkResult.failures.slice(0, 4).map((failure) => (
                  <div key={failure.contact_id || failure.staff_id}>
                    {failure.name}: {failure.message}
                  </div>
                ))}
                {bulkResult.failures.length > 4 ? <div>+{bulkResult.failures.length - 4} more failures</div> : null}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
